import { createSignal, For, JSXElement } from 'solid-js';
import { Localized } from '~/i18n';

import { ModalSizes } from '~/components/ui/Modal/Modal.tsx';
import Modal from '~/components/ui/Modal';
import {
  CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS,
  CUSTOMIZATION_ADDON_NAME,
  CUSTOMIZATION_PRO_ADDON_ID,
  getCustomizationAddonPrice,
  getTranslationKey
} from '~/pages/Billing/utils.ts';
import { Tag } from '~/components/ui/Tag';
import { Button } from '~/components/ui/Button';
import { useWorkspaceBillingContext } from '~/pages/Billing/contexts/WorkspaceBillingContext.tsx';
import { useToast } from '~/components/ui/Toast';
import { getLocalizedString } from '~/i18n/utils.ts';
import { useIntl } from '@cookbook/solid-intl';
import { addProductToSubscription } from '~/api';

export default function UpgradeCustomizationAddonModal(): JSXElement {
  const workspaceBillingContext = useWorkspaceBillingContext();
  const intl = useIntl();
  const toaster = useToast();
  const [isCtaLoading, setIsCtaLoading] = createSignal(false);
  return (
    <Modal
      title={
        <div class={`flex items-center`}>
          <Localized
            translationKey={getTranslationKey(
              'customization_addon_modal_header'
            )}
            vars={{
              customizationAddonName: CUSTOMIZATION_ADDON_NAME
            }}
          />
          <Tag class={`text-sm font-medium`}>
            <Localized
              translationKey={getTranslationKey(`customization_addon_tag`)}
            />
          </Tag>
          <span class={`ml-auto text-sm font-medium`}>
            {getCustomizationAddonPrice()}
          </span>
        </div>
      }
      open={workspaceBillingContext.shouldShowCustomizationUpgradeModal}
      size={ModalSizes.LARGE}
    >
      <div class={`mt-4 text-md`}>
        <Localized
          translationKey={getTranslationKey(
            `customization_addon_description_in_modal`
          )}
          vars={{
            customizationAddonName: CUSTOMIZATION_ADDON_NAME
          }}
        />
      </div>
      <div class={`mt-6`}>
        <h6 class={`text-sm text-fg-default`}>
          <Localized
            translationKey={getTranslationKey(
              `customization_addon_features_title`
            )}
          />
        </h6>
        <ul class={`mt-2.5 flex flex-wrap items-center gap-2`}>
          <For each={CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS}>
            {(key) => (
              <li>
                <Tag
                  class={`mx-0 py-2 px-3 rounded-3xl text-gray-700 font-medium border border-[#BECEF4]`}
                >
                  <Localized translationKey={getTranslationKey(key)} />
                </Tag>
              </li>
            )}
          </For>
        </ul>
      </div>
      <div class={`w-full mt-10 flex justify-end gap-3`}>
        <Button
          variant={`outline`}
          size={`sm`}
          onClick={() => {
            workspaceBillingContext.hideCustomizationUpgradeModal();
          }}
        >
          <Localized translationKey={`cta_labels.cancel`} />
        </Button>
        <Button
          size={`sm`}
          loading={isCtaLoading()}
          onClick={async () => {
            try {
              setIsCtaLoading(true);
              await addProductToSubscription({
                productName: CUSTOMIZATION_PRO_ADDON_ID
              });
              await workspaceBillingContext.refetchBillingInfo?.();
              toaster.toast().success({
                description: getLocalizedString(
                  getTranslationKey(`customization_add_upgrade_success_toast`),
                  { intl }
                )
              });
              workspaceBillingContext.hideCustomizationUpgradeModal();
            } catch (e) {
              toaster.toast().error({
                description: getLocalizedString(
                  getTranslationKey(`customization_add_upgrade_failure_toast`),
                  { intl }
                )
              });
            } finally {
              setIsCtaLoading(false);
            }
          }}
        >
          <Localized
            translationKey={getTranslationKey(`modal_cta_purchase_addon`)}
          />
        </Button>
      </div>
    </Modal>
  );
}
